<template>
  <NavBar pageName="SIM Details"></NavBar>
  <v-container style="margin-top: 185px;">
    <v-btn variant="text" color="primary" class="mb-4" @click="$router.back()">
      <v-icon class="mr-1">mdi-arrow-left</v-icon>
      Back
    </v-btn>

    <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

    <v-alert v-if="errorMessage" type="error" class="mb-4">{{ errorMessage }}</v-alert>

    <template v-if="sim && !loading">
      <!-- SIM Information Card -->
      <v-card class="mb-4">
        <v-card-title class="bg-primary text-white d-flex align-center">
          <v-icon class="mr-2">mdi-sim</v-icon>
          Informatii SIM
          <v-spacer></v-spacer>
          <v-chip :color="getStatusColor(sim.sim_status_name)" class="ml-2">
            {{ sim.sim_status_name }}
          </v-chip>
        </v-card-title>
        <v-card-text class="pa-4">
          <v-row>
            <v-col cols="12" md="6">
              <v-table density="compact">
                <tbody>
                  <tr>
                    <td class="font-weight-medium" width="40%">SIM ID</td>
                    <td class="font-weight-bold">{{ sim.id }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Status Retea</td>
                    <td>
                      <v-chip size="small" :color="getStatusColor(sim.sim_status_name)">
                        {{ sim.sim_status_name || '-' }}
                      </v-chip>
                    </td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Subscriber ID</td>
                    <td>{{ sim.subscriber_id || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">SubscriberID (extern)</td>
                    <td>{{ sim.subscriberID || '-' }}</td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
            <v-col cols="12" md="6">
              <v-table density="compact">
                <tbody>
                  <tr>
                    <td class="font-weight-medium" width="40%">NUI</td>
                    <td>{{ sim.nui || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Seria Echipament</td>
                    <td>{{ sim.seria_echipament || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Technician</td>
                    <td>{{ sim.technician || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Creat la</td>
                    <td>{{ formatDateTime(sim.created_at) }}</td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
          </v-row>
          <div v-if="sim.description" class="mt-3 pa-2 bg-grey-lighten-4 rounded">
            <span class="font-weight-medium">Descriere:</span> {{ sim.description }}
          </div>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-btn
            v-if="sim.imsi_id"
            color="primary"
            variant="outlined"
            :loading="updatingImsiInfo"
            @click="updateSimSubscriberInfo"
          >
            <v-icon class="mr-1">mdi-refresh</v-icon>
            Update mobile network info
          </v-btn>
          <v-btn
            color="info"
            variant="outlined"
            :to="`/comments?comment_type_id=3&element_id=${sim.id}`"
          >
            <v-icon class="mr-1">mdi-comment-text-multiple</v-icon>
            {{ t.comments?.view_comments || 'View Comments' }}
          </v-btn>
          <v-btn
            v-if="hasImsiSubscription"
            color="orange"
            variant="outlined"
            :to="trafficLink"
          >
            <v-icon class="mr-1">mdi-chart-timeline-variant</v-icon>
            Trafic
          </v-btn>
          <v-btn
            v-if="sim.sim_status_name === 'active' && sim.subscriber_id"
            color="deep-orange"
            variant="outlined"
            :to="`/sim/${sim.id}/network-services`"
          >
            <v-icon class="mr-1">mdi-radio-tower</v-icon>
            Servicii Retea
          </v-btn>
        </v-card-actions>
        <v-alert
          v-if="updateImsiInfoMessage"
          :type="updateImsiInfoSuccess ? 'success' : 'error'"
          density="compact"
          class="mx-4 mb-4"
          closable
          @click:close="updateImsiInfoMessage = ''"
        >
          {{ updateImsiInfoMessage }}
        </v-alert>
      </v-card>

      <!-- IMSI Information Card -->
      <v-card class="mb-4">
        <v-card-title class="bg-indigo text-white d-flex align-center">
          <v-icon class="mr-2">mdi-cellphone-wireless</v-icon>
          Informatii IMSI
          <v-spacer></v-spacer>
          <v-chip v-if="sim.mno_status_name" color="white" text-color="indigo" class="ml-2">
            {{ sim.mno_status_name }}
          </v-chip>
        </v-card-title>
        <v-card-text class="pa-4">
          <v-row>
            <v-col cols="12" md="6">
              <v-table density="compact">
                <tbody>
                  <tr>
                    <td class="font-weight-medium" width="40%">IMSI ID</td>
                    <td>{{ sim.imsi_id || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">IMSI</td>
                    <td class="font-weight-bold text-indigo">{{ sim.imsi_number || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Serial</td>
                    <td>{{ sim.serial || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Reseller ID</td>
                    <td>{{ sim.reseller_id || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Tip IMSI</td>
                    <td>{{ sim.imsi_type_name || '-' }}</td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
            <v-col cols="12" md="6">
              <v-table density="compact">
                <tbody>
                  <tr>
                    <td class="font-weight-medium" width="40%">Retea</td>
                    <td>{{ sim.network_name || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Status MNO</td>
                    <td>{{ sim.mno_status_name || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">PIN1 / PIN2</td>
                    <td>{{ sim.imsi_pin1 || '-' }} / {{ sim.imsi_pin2 || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">PUK1 / PUK2</td>
                    <td>{{ sim.imsi_puk1 || '-' }} / {{ sim.imsi_puk2 || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Creat la</td>
                    <td>{{ formatDateTime(sim.imsi_created_at) }}</td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- MSISDN Information Card -->
      <v-card class="mb-4">
        <v-card-title class="bg-teal-darken-1 text-white d-flex align-center">
          <v-icon class="mr-2">mdi-phone</v-icon>
          Informatii MSISDN
        </v-card-title>
        <v-card-text class="pa-4">
          <v-row>
            <v-col cols="12" md="6">
              <v-table density="compact">
                <tbody>
                  <tr>
                    <td class="font-weight-medium" width="40%">MSISDN ID</td>
                    <td>{{ sim.msisdn_id || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">MSISDN</td>
                    <td class="font-weight-bold text-teal">{{ sim.msisdn || '-' }}</td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
            <v-col cols="12" md="6">
              <v-table density="compact">
                <tbody>
                  <tr>
                    <td class="font-weight-medium" width="40%">Provisioning MSISDN ID</td>
                    <td>{{ sim.provisioning_msisdn_id || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Provisioning IMSI ID</td>
                    <td>{{ sim.provisioning_imsi_id || '-' }}</td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Client Information Card -->
      <v-card class="mb-4">
        <v-card-title class="bg-orange-darken-1 text-white d-flex align-center">
          <v-icon class="mr-2">mdi-domain</v-icon>
          Informatii Client
          <v-spacer></v-spacer>
          <v-chip v-if="sim.client_status_name" color="white" text-color="orange-darken-1" class="ml-2">
            {{ sim.client_status_name }}
          </v-chip>
        </v-card-title>
        <v-card-text class="pa-4">
          <v-row>
            <v-col cols="12" md="6">
              <v-table density="compact">
                <tbody>
                  <tr>
                    <td class="font-weight-medium" width="40%">Client ID</td>
                    <td>{{ sim.client_id || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Nume</td>
                    <td class="font-weight-bold">{{ sim.client_name || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Nume scurt</td>
                    <td>{{ sim.client_short_name || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Tip Client</td>
                    <td>{{ sim.client_type_name || '-' }}</td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
            <v-col cols="12" md="6">
              <v-table density="compact">
                <tbody>
                  <tr>
                    <td class="font-weight-medium" width="40%">Telefon</td>
                    <td>{{ sim.client_phone || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Email</td>
                    <td>{{ sim.client_email || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Tip Facturare</td>
                    <td>{{ sim.client_billing_type_name || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Status</td>
                    <td>{{ sim.client_status_name || '-' }}</td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-btn
            color="orange-darken-1"
            variant="outlined"
            @click="navigateToClientEdit(sim.client_id)"
          >
            <v-icon class="mr-1">mdi-account-edit</v-icon>
            Pagina Client
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- Traffic Usage Comparison -->
      <TrafficUsageComparison
        v-if="hasImsiSubscription"
        :simId="sim.id"
        :token="token"
        period="current_month"
      />

      <!-- Limitation Profile Card -->
      <v-card class="mb-4">
        <v-card-title class="bg-deep-purple text-white d-flex align-center">
          <v-icon class="mr-2">mdi-shield-check</v-icon>
          Profil Limitari
          <v-spacer></v-spacer>
          <v-chip v-if="limitationProfile" :color="limitationProfile.enabled ? 'success' : 'grey'" size="small" class="ml-2">
            {{ limitationProfile.enabled ? 'Activ' : 'Inactiv' }}
          </v-chip>
        </v-card-title>
        <v-card-text class="pa-4">
          <v-progress-linear v-if="loadingProfile" indeterminate color="deep-purple" class="mb-2" />
          <template v-if="!loadingProfile && limitationProfile">
            <v-table density="compact">
              <tbody>
                <tr>
                  <td class="font-weight-medium" width="40%">Profil</td>
                  <td class="font-weight-bold text-deep-purple">{{ limitationProfile.name }}</td>
                </tr>
                <tr v-if="limitationProfile.description">
                  <td class="font-weight-medium">Descriere</td>
                  <td>{{ limitationProfile.description }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">Reguli</td>
                  <td>{{ limitationProfile.rules_count }} reguli configurate</td>
                </tr>
              </tbody>
            </v-table>
          </template>
          <template v-if="!loadingProfile && !limitationProfile">
            <v-alert type="info" variant="tonal" density="compact">
              Niciun profil de limitari asignat acestui SIM.
            </v-alert>
          </template>
          <v-alert v-if="profileMessage" :type="profileSuccess ? 'success' : 'error'" density="compact" class="mt-2" closable @click:close="profileMessage = ''">
            {{ profileMessage }}
          </v-alert>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-btn
            color="deep-purple"
            :variant="limitationProfile ? 'outlined' : 'elevated'"
            @click="showSelectProfileDialog = true"
          >
            <v-icon class="mr-1">{{ limitationProfile ? 'mdi-swap-horizontal' : 'mdi-plus' }}</v-icon>
            {{ limitationProfile ? 'Schimba' : 'Asigneaza profil' }}
          </v-btn>
          <v-btn
            v-if="limitationProfile"
            color="error"
            variant="outlined"
            @click="confirmRemoveProfile"
          >
            <v-icon class="mr-1">mdi-delete</v-icon>
            Elimina
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="deep-purple"
            variant="text"
            to="/traffic-alerts?tab=profiles"
          >
            <v-icon class="mr-1">mdi-open-in-new</v-icon>
            Vezi toate profilele
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- Select Limitation Profile Dialog -->
      <SelectLimitationProfileDialog
        v-model="showSelectProfileDialog"
        :currentProfileId="limitationProfile?.id || null"
        @select="onProfileSelected"
      />

      <!-- Confirm Remove Profile Dialog -->
      <v-dialog v-model="showRemoveProfileDialog" max-width="400px">
        <v-card>
          <v-card-title class="bg-error text-white">Confirmare</v-card-title>
          <v-card-text class="pa-4">
            Sigur doriti sa eliminati profilul <strong>{{ limitationProfile?.name }}</strong> de pe acest SIM?
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn variant="text" @click="showRemoveProfileDialog = false">Anuleaza</v-btn>
            <v-btn color="error" variant="elevated" :loading="removingProfile" @click="doRemoveProfile">Elimina</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Services Link Card -->
      <v-card class="mb-4">
        <v-card-title class="bg-purple text-white d-flex align-center">
          <v-icon class="mr-2">mdi-cog</v-icon>
          Servicii
        </v-card-title>
        <v-card-text class="pa-4">
          <v-btn
            color="purple"
            variant="elevated"
            @click="navigateToSimServices"
          >
            <v-icon class="mr-1">mdi-format-list-bulleted</v-icon>
            Vezi toate serviciile acestui SIM
          </v-btn>
        </v-card-text>
      </v-card>

      <!-- Inventory Section -->
      <v-card class="mb-4">
        <v-card-title class="bg-teal text-white d-flex align-center">
          <v-icon class="mr-2">mdi-package-variant</v-icon>
          Inventory
          <v-spacer></v-spacer>
          <v-btn
            v-if="inventory"
            size="small"
            variant="outlined"
            color="white"
            @click="showHistoryDialog = true"
          >
            <v-icon class="mr-1">mdi-history</v-icon>
            View History
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <template v-if="inventory">
            <v-row>
              <v-col cols="12" md="6">
                <v-table density="compact">
                  <tbody>
                    <tr>
                      <td class="font-weight-medium" width="40%">Status</td>
                      <td>
                        <v-chip :color="getInventoryStatusColor(inventory.inventory_status_name)" size="small">
                          {{ inventory.inventory_status_name }}
                        </v-chip>
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-medium">Location</td>
                      <td class="font-weight-bold">{{ inventory.location_client_name || 'Not assigned' }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-medium">Shipping Date</td>
                      <td>{{ formatDate(inventory.shipping_date) }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-medium">Tracking Number</td>
                      <td>{{ inventory.tracking_number || '-' }}</td>
                    </tr>
                  </tbody>
                </v-table>
              </v-col>
              <v-col cols="12" md="6">
                <v-table density="compact">
                  <tbody>
                    <tr>
                      <td class="font-weight-medium" width="40%">Expected Delivery</td>
                      <td>{{ formatDate(inventory.expected_delivery_date) }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-medium">Received Date</td>
                      <td>{{ formatDate(inventory.received_date) }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-medium">Last Updated</td>
                      <td>{{ formatDateTime(inventory.updated_at) }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-medium">Updated By</td>
                      <td>{{ inventory.updated_by_user_name || '-' }}</td>
                    </tr>
                  </tbody>
                </v-table>
              </v-col>
            </v-row>
            <div v-if="inventory.notes" class="mt-3">
              <span class="font-weight-medium">Notes:</span> {{ inventory.notes }}
            </div>
          </template>
          <template v-else>
            <v-alert type="info" variant="tonal" density="compact">
              No inventory record found for this SIM.
              <router-link to="/goodsInventory" class="ml-2">Add inventory record</router-link>
            </v-alert>
          </template>
        </v-card-text>
      </v-card>

      <!-- Recent Inventory History (preview) -->
      <v-card v-if="inventoryLogs && inventoryLogs.length > 0" class="mb-4">
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-history</v-icon>
          Recent Inventory History
        </v-card-title>
        <v-card-text>
          <v-data-table
            :headers="logHeaders"
            :items="inventoryLogs"
            :items-per-page="5"
            density="compact"
            class="elevation-1"
          >
            <template v-slot:[`item.from_inventory_status_name`]="{ item }">
              <v-chip v-if="item.from_inventory_status_name" size="x-small" :color="getInventoryStatusColor(item.from_inventory_status_name)">
                {{ item.from_inventory_status_name }}
              </v-chip>
              <span v-else>-</span>
            </template>
            <template v-slot:[`item.to_inventory_status_name`]="{ item }">
              <v-chip size="x-small" :color="getInventoryStatusColor(item.to_inventory_status_name)">
                {{ item.to_inventory_status_name }}
              </v-chip>
            </template>
            <template v-slot:[`item.created_at`]="{ item }">
              {{ formatDateTime(item.created_at) }}
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </template>

    <!-- Subscriber Status Dialog -->
    <SubscriberStatusDialog
      :show="showSubscriberStatusDialog"
      :data="subscriberStatusData"
      :loading="loadingSubscriberStatus"
      @close="showSubscriberStatusDialog = false"
    />

    <!-- Full History Dialog -->
    <v-dialog v-model="showHistoryDialog" max-width="900px">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center bg-teal text-white">
          <span>
            <v-icon class="mr-2">mdi-history</v-icon>
            Inventory History - {{ sim?.imsi_number }}
          </span>
          <v-btn icon size="small" variant="text" color="white" @click="showHistoryDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <v-data-table
            :headers="fullLogHeaders"
            :items="inventoryLogs"
            :items-per-page="10"
            class="elevation-1"
          >
            <template v-slot:[`item.from_inventory_status_name`]="{ item }">
              <v-chip v-if="item.from_inventory_status_name" size="small" :color="getInventoryStatusColor(item.from_inventory_status_name)">
                {{ item.from_inventory_status_name }}
              </v-chip>
              <span v-else>-</span>
            </template>
            <template v-slot:[`item.to_inventory_status_name`]="{ item }">
              <v-chip size="small" :color="getInventoryStatusColor(item.to_inventory_status_name)">
                {{ item.to_inventory_status_name }}
              </v-chip>
            </template>
            <template v-slot:[`item.created_at`]="{ item }">
              {{ formatDateTime(item.created_at) }}
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import NavBar from "@/components/NavBar.vue";
import SubscriberStatusDialog from "@/components/dialogs/SubscriberStatusDialog.vue";
import TrafficUsageComparison from "@/components/traffic/TrafficUsageComparison.vue";
import SelectLimitationProfileDialog from "@/components/dialogs/SelectLimitationProfileDialog.vue";
import { getSimLimitationProfile, assignSimProfile, removeSimProfile } from "@/composables/useTrafficAlertsAPI";
import store from "@/store";
import { log } from "@/composables/useLog";
import { getSimDetails } from "@/composables/useGetSimsAPI";
import { updateImsiInfo } from "@/composables/useGetAllocatedImsisAPI";
import { t as tRef } from "@/composables/useLanguages";

export default defineComponent({
  name: 'SimDetails',
  components: {
    NavBar,
    SubscriberStatusDialog,
    TrafficUsageComparison,
    SelectLimitationProfileDialog
  },
  data() {
    return {
      loading: false,
      errorMessage: '',
      sim: null,
      inventory: null,
      inventoryLogs: [],
      showHistoryDialog: false,
      // Update IMSI Info
      updatingImsiInfo: false,
      updateImsiInfoMessage: '',
      updateImsiInfoSuccess: false,
      // Subscriber Status Dialog
      showSubscriberStatusDialog: false,
      loadingSubscriberStatus: false,
      subscriberStatusData: null,
      // Limitation Profile
      limitationProfile: null,
      loadingProfile: false,
      profileMessage: '',
      profileSuccess: false,
      showSelectProfileDialog: false,
      showRemoveProfileDialog: false,
      removingProfile: false
    }
  },
  computed: {
    t() {
      return tRef.value;
    },
    token() {
      return store.getters.getToken;
    },
    simId() {
      return this.$route.params.id;
    },
    hasImsiSubscription() {
      return this.sim && this.sim.subscriber_id
    },
    trafficLink() {
      const params = new URLSearchParams()
      if (this.sim?.id) params.append('sim_id', this.sim.id)
      if (this.sim?.client_id) params.append('client_id', this.sim.client_id)
      return `/reports/traffic?${params.toString()}`
    },
    logHeaders() {
      return [
        { title: 'Date', key: 'created_at', width: '150px' },
        { title: 'From', key: 'from_inventory_status_name', width: '120px' },
        { title: 'To', key: 'to_inventory_status_name', width: '120px' },
        { title: 'Location', key: 'to_client_name' },
        { title: 'By', key: 'created_by_user_name', width: '120px' }
      ];
    },
    fullLogHeaders() {
      return [
        { title: 'Date', key: 'created_at', width: '150px' },
        { title: 'From Status', key: 'from_inventory_status_name', width: '120px' },
        { title: 'To Status', key: 'to_inventory_status_name', width: '120px' },
        { title: 'From Location', key: 'from_client_name' },
        { title: 'To Location', key: 'to_client_name' },
        { title: 'Tracking #', key: 'tracking_number', width: '120px' },
        { title: 'Notes', key: 'notes' },
        { title: 'By', key: 'created_by_user_name', width: '100px' }
      ];
    }
  },
  methods: {
    async loadSimDetails() {
      if (!this.simId) {
        this.errorMessage = 'No SIM ID provided';
        return;
      }

      this.loading = true;
      this.errorMessage = '';

      try {
        const response = await getSimDetails(this.token, this.simId);
        log("SimDetails response:", response);

        if (response.error) {
          this.errorMessage = response.message || 'Error loading SIM details';
        } else {
          this.sim = response.sim;
          this.inventory = response.inventory;
          this.inventoryLogs = response.inventory_logs || [];
        }
      } catch (err) {
        log("Error loading SIM details:", err);
        this.errorMessage = 'Error loading SIM details';
      } finally {
        this.loading = false;
      }
    },

    getStatusColor(status) {
      const colors = {
        'active': 'success',
        'provisioned': 'success',
        'not_provisioned': 'warning',
        'suspended': 'orange',
        'terminated': 'error'
      };
      return colors[status] || 'grey';
    },

    getInventoryStatusColor(status) {
      const colors = {
        'in_stock': 'success',
        'pending_shipment': 'warning',
        'in_transit': 'info',
        'delivered': 'primary'
      };
      return colors[status] || 'grey';
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },

    isExpiringSoon(dateString) {
      if (!dateString) return false;
      const expirationDate = new Date(dateString);
      const now = new Date();
      const daysUntilExpiration = (expirationDate - now) / (1000 * 60 * 60 * 24);
      return daysUntilExpiration <= 7 && daysUntilExpiration > 0;
    },

    navigateToClientEdit(clientId) {
      if (!clientId) return;
      this.$router.push({ name: 'editClient', query: { id: clientId } });
    },

    navigateToSimServices() {
      if (!this.sim?.id) return;
      this.$router.push({ path: '/clientServices', query: { sim_id: this.sim.id } });
    },

    async updateSimSubscriberInfo() {
      if (!this.sim || !this.sim.imsi_id) {
        this.updateImsiInfoMessage = 'No IMSI ID available for this SIM';
        this.updateImsiInfoSuccess = false;
        return;
      }

      // Open dialog and show loading
      this.showSubscriberStatusDialog = true;
      this.loadingSubscriberStatus = true;
      this.subscriberStatusData = null;
      this.updatingImsiInfo = true;
      this.updateImsiInfoMessage = '';

      try {
        const result = await updateImsiInfo(this.token, this.sim.imsi_id);

        // Store the full result for the dialog
        this.subscriberStatusData = result;

        if (!result.error) {
          this.updateImsiInfoMessage = result.message || 'Info actualizat cu succes';
          this.updateImsiInfoSuccess = true;
          // Reload the details to show updated info
          await this.loadSimDetails();
        } else {
          this.updateImsiInfoMessage = result.message || 'Eroare la actualizare info';
          this.updateImsiInfoSuccess = false;
        }
      } catch (err) {
        log('Error updating IMSI info:', err);
        this.updateImsiInfoMessage = 'Eroare la actualizare info';
        this.updateImsiInfoSuccess = false;
        this.subscriberStatusData = { error: true, message: 'Eroare la actualizare info' };
      } finally {
        this.updatingImsiInfo = false;
        this.loadingSubscriberStatus = false;
      }
    },

    async loadLimitationProfile() {
      if (!this.simId || !this.token) return;
      this.loadingProfile = true;
      try {
        const res = await getSimLimitationProfile(this.token, this.simId);
        if (!res.error) {
          this.limitationProfile = res.profile || null;
        }
      } catch (err) {
        log("Error loading limitation profile:", err);
      } finally {
        this.loadingProfile = false;
      }
    },

    async onProfileSelected(profileId) {
      this.profileMessage = '';
      this.loadingProfile = true;
      try {
        const res = await assignSimProfile(this.token, this.simId, profileId);
        if (!res.error) {
          this.limitationProfile = res.profile || null;
          this.profileMessage = res.message || 'Profil asignat cu succes';
          this.profileSuccess = true;
        } else {
          this.profileMessage = res.message || 'Eroare la asignare';
          this.profileSuccess = false;
        }
      } catch (err) {
        this.profileMessage = 'Eroare la asignare profil';
        this.profileSuccess = false;
      } finally {
        this.loadingProfile = false;
      }
    },

    confirmRemoveProfile() {
      this.showRemoveProfileDialog = true;
    },

    async doRemoveProfile() {
      this.removingProfile = true;
      this.profileMessage = '';
      try {
        const res = await removeSimProfile(this.token, this.simId);
        if (!res.error) {
          this.limitationProfile = null;
          this.profileMessage = res.message || 'Profil eliminat cu succes';
          this.profileSuccess = true;
        } else {
          this.profileMessage = res.message || 'Eroare la eliminare';
          this.profileSuccess = false;
        }
      } catch (err) {
        this.profileMessage = 'Eroare la eliminare profil';
        this.profileSuccess = false;
      } finally {
        this.removingProfile = false;
        this.showRemoveProfileDialog = false;
      }
    },

  },
  mounted() {
    this.loadSimDetails();
    this.loadLimitationProfile();
  }
});
</script>
